<script lang="ts" setup>
import RadioButton from '#components/elements/radioButton/RadioButton.vue';
import { useLanguage } from '#composables/useLanguage';
import {
  type CarrefourReferentialIntervalType,
  type ComparisonPeriodModeType,
  type CustomDateType,
  type DatePeriodIntervalType,
  type NielsenPeriodIntervalType,
  type ProductTaxonomyType,
  type ReferentialIntervalsType
} from '@carrefour-gcs/shared';
import { computed } from 'vue';
import CustomIntervalSelector from './CustomIntervalSelector.vue';
import type { SelectedIntervalType } from './periodSelector.model';

const props = defineProps<{
  taxonomy: ProductTaxonomyType;
  referentialIntervals: ReferentialIntervalsType;
  carrefourReferentialIntervals: keyof CarrefourReferentialIntervalType;
  referencePeriod: DatePeriodIntervalType | NielsenPeriodIntervalType;
  comparisonMode: ComparisonPeriodModeType;
  interval: { start: CustomDateType; end: CustomDateType };
  selectedInterval: SelectedIntervalType;
  availableInterval: { start: Date; end: Date };
  isInvalid?: boolean;
}>();

const emit = defineEmits<{
  'update:comparisonMode': [comparisonMode: ComparisonPeriodModeType];
  'update:interval': [interval: SelectedIntervalType];
}>();

const { t } = useLanguage();

const isCustomModeActive = computed(() => props.comparisonMode === 'custom');
</script>

<template>
  <div class="w-full border-t border-default-very-light mt-6 pt-6 text-left">
    <p class="text-lg font-bold mb-4">{{ t('timeSelectorPopin.subtitleComparisonPeriod') }}</p>

    <RadioButton
      name="comparison-mode"
      value="default"
      :label="t('timeSelectorPopin.comparisonDefaultPeriod')"
      :checked="comparisonMode === 'previousYear'"
      @check="emit('update:comparisonMode', 'previousYear')"
    />

    <CustomIntervalSelector
      name="comparison-mode"
      :label="t('timeSelectorPopin.comparisonCustomPeriod')"
      :is-active="isCustomModeActive"
      :period="referencePeriod.period"
      :available-interval="availableInterval"
      :selected-interval="selectedInterval"
      :nielsen-unavailable-message="t('timeSelectorPopin.comparisonCustomPeriodNotAvailableForNielsen')"
      :hide-component-when-inactive="true"
      :is-invalid="isInvalid"
      :error-message="isInvalid ? t('timeSelectorPopin.comparisonPeriodInvalid') : undefined"
      @select="emit('update:comparisonMode', 'custom')"
      @update:interval="emit('update:interval', $event)"
    />
  </div>
</template>
